import { useQuery } from '@tanstack/react-query';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaUserCircle } from 'react-icons/fa';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getAffiliateProfile } from '../../../Services/Affiliate/Dashboard';
import { getFlagFromCountryCode, countriesWithdialingCodesAndFlag } from '../../../Utils/Utils';
import './AffiliateProfile.css';

const AffiliateProfile = () => {
  usePageTitle('My Profile');
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  // Fetch affiliate profile data
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['affiliateProfile'],
    queryFn: getAffiliateProfile,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Split full_name into first and last name
  const getFirstName = () => {
    if (!profileData?.full_name) return '-';
    const nameParts = profileData.full_name.trim().split(' ');
    return nameParts[0] || '-';
  };

  const getLastName = () => {
    if (!profileData?.full_name) return '-';
    const nameParts = profileData.full_name.trim().split(' ');
    return nameParts.slice(1).join(' ') || '-';
  };

  // Format phone number with dial code
  const formatPhoneNumber = () => {
    if (!profileData?.phone) return '-';
    const dialCode = profileData.dial_code ? `+${profileData.dial_code}` : '';
    return dialCode ? `${dialCode} ${profileData.phone}` : profileData.phone;
  };

  // Get country flag from country code
  const getCountryFlag = () => {
    if (!profileData?.country_code) return null;
    try {
      const countryCode = profileData.country_code.toUpperCase();
      const country = countriesWithdialingCodesAndFlag?.find(
        (item) => item.code === countryCode
      );
      if (country && country.flagEmoji) {
        return getFlagFromCountryCode(country.flagEmoji, 'small');
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className="affiliate-profile-container">
        <h2 className="screen-title mb-0">My Profile</h2>
        <div className="text-center py-5">
          <p className="text-muted">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="affiliate-profile-container">
      <h2 className="screen-title mb-4">My Profile</h2>
      
      <div className="affiliate-profile-card">
        {/* Orange Banner */}
        <div className="profile-banner"></div>
        
        {/* Profile Picture */}
        <div className="profile-picture-container justify-content-start ps-4">
          {profileData?.image ? (
            <img
              src={`${imageBaseUrl}${profileData.image}`}
              alt={profileData.full_name || 'Profile'}
              className="profile-picture"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : null}
          <FaUserCircle 
            className="profile-picture" 
            style={{ display: profileData?.image ? 'none' : 'block' }}
          />
        </div>

        {/* User Information */}
        <div className="profile-info-section">
          <Row>
            <Col xs={12} sm={6} className="mb-4">
              <div className="profile-detail-item">
                <label>First Name:</label>
                <p>{getFirstName()}</p>
              </div>
            </Col>
            <Col xs={12} sm={6} className="mb-4">
              <div className="profile-detail-item">
                <label>Last Name:</label>
                <p>{getLastName()}</p>
              </div>
            </Col>
            <Col xs={12} sm={6} className="mb-4">
              <div className="profile-detail-item">
                <label>Email Address:</label>
                <p>{profileData?.email || '-'}</p>
              </div>
            </Col>
            <Col xs={12} sm={6} className="mb-4">
              <div className="profile-detail-item">
                <label>Phone Number:</label>
                <div className="phone-number-wrapper">
                  {getCountryFlag() && (
                    <span className="country-flag">{getCountryFlag()}</span>
                  )}
                  <p>{formatPhoneNumber()}</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <div className="profile-actions">
          <Link
            to="/affiliate/profile/edit-profile"
            className="btn btn-primary edit-profile-btn"
          >
            Edit Profile
          </Link>
          <Link
            to="/affiliate/profile/change-password"
            className="btn btn-secondary change-password-btn"
          >
            Change Password
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AffiliateProfile;
